<template>
    <div class="w-full flex mt-3" :class="isMine ? 'justify-end' : 'justify-start'">
        <div class="max-w-[85%] sm:max-w-[75%] md:max-w-[70%] lg:max-w-[60%]">
            <div class="p-3 sm:p-4 border rounded-lg text-sm sm:text-base text-[#131523] inline-block"
                :class="getMessageClasses">
                <div v-if="message.message && message.message.trim()" class="break-words markdown-content" v-html="renderMarkdown(message.message)">
                </div>

                <div v-if="message.attachments && message.attachments.length"
                    :class="{ 'mt-3': message.message && message.message.trim() }" class="space-y-3">
                    <div v-for="attachment in message.attachments" :key="attachment.id || attachment.url"
                        class="flex items-center gap-3">
                        <Image v-if="attachment.type && attachment.type.startsWith('image/')" :src="attachment.url"
                            preview class="aspect-video w-full max-w-[280px] sm:max-w-full"
                            imageClass="h-full mx-auto rounded-lg" alt="Pré-visualização do anexo" />
                        <a v-else :href="attachment.url" target="_blank" rel="noopener"
                            :download="attachment.name || true"
                            class="flex items-center gap-2 text-xs sm:text-sm text-[#4C4C9B] underline break-all">
                            <font-awesome-icon icon="fa-paperclip" class="flex-shrink-0" />
                            <span class="truncate max-w-[180px] sm:max-w-[220px]">
                                {{ shortenFileName(attachment.name) || 'Baixar anexo' }}
                            </span>
                        </a>
                    </div>
                </div>

                <div class="flex items-center justify-between mt-2">
                    <div class="flex items-center gap-1.5">
                        <!-- Indicador para mensagens enviadas -->
                        <template v-if="isMine">
                            <span v-if="message.read_at" class="text-blue-600"
                                :title="`Lida em ${formatTime(message.read_at)}`">
                                <font-awesome-icon icon="fa-check-double" class="text-xs" />
                            </span>
                            <span v-else class="text-[#9C9C9C]" title="Enviada">
                                <font-awesome-icon icon="fa-check" class="text-xs" />
                            </span>
                        </template>

                        <!-- Indicador para mensagens recebidas -->
                        <template v-else>
                            <span v-if="!message.read_at"
                                class="inline-flex items-center gap-1 text-blue-600 text-xs font-semibold">
                                <span class="w-2 h-2 bg-blue-600 rounded-full"></span>
                                Nova
                            </span>
                        </template>
                    </div>
                    <p class="text-[#9C9C9C] text-xs">
                        {{ formatTime(message.created_at) }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import Image from 'primevue/image'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const shortenFileName = (name = '', maxLength = 28) => {
    if (!name) return ''
    if (name.length <= maxLength) return name

    const lastDotIndex = name.lastIndexOf('.')
    const extension = lastDotIndex > 0 ? name.slice(lastDotIndex) : ''
    const baseName = lastDotIndex > 0 ? name.slice(0, lastDotIndex) : name
    const baseMaxLength = Math.max(maxLength - extension.length - 3, 1)

    return `${baseName.slice(0, baseMaxLength)}...${extension}`
}

// Configurar marked
marked.setOptions({
    breaks: true, // Interpreta quebras de linha simples como <br>
    gfm: true, // GitHub Flavored Markdown
})

export default {
    components: {
        Image
    },

    props: {
        message: {
            type: Object,
            required: true
        },
        isMine: {
            type: Boolean,
            default: false
        }
    },

    setup(props) {
        const getMessageClasses = computed(() => {
            if (props.isMine) {
                return 'bg-[#F7F7F9] border-[#EAEAEA]'
            }

            return !props.message.read_at && !props.isMine
                ? 'border-blue-500 bg-blue-50'
                : 'border-[#EAEAEA] bg-white'
        })

        const formatTime = (date) => {
            if (!date) return ''
            const d = new Date(date)
            return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
        }

        const renderMarkdown = (text) => {
            if (!text) return ''
            // Converte Markdown para HTML
            const html = marked.parse(text)
            // Sanitiza o HTML para prevenir XSS
            return DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
                ALLOWED_ATTR: ['href', 'target', 'rel']
            })
        }

        return {
            getMessageClasses,
            formatTime,
            shortenFileName,
            renderMarkdown
        }
    }
}
</script>

<style scoped>
/* Estilos para conteúdo Markdown */
.markdown-content :deep(p) {
    margin: 0;
    padding: 0;
}

.markdown-content :deep(p + p) {
    margin-top: 0.5rem;
}

.markdown-content :deep(strong) {
    font-weight: 600;
    color: inherit;
}

.markdown-content :deep(em) {
    font-style: italic;
}

.markdown-content :deep(u) {
    text-decoration: underline;
}

.markdown-content :deep(s) {
    text-decoration: line-through;
}

.markdown-content :deep(a) {
    color: #4C4C9B;
    text-decoration: underline;
}

.markdown-content :deep(a:hover) {
    color: #3a3a7a;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
}

.markdown-content :deep(li) {
    margin: 0.25rem 0;
}

.markdown-content :deep(blockquote) {
    border-left: 3px solid #d1d5db;
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: #6b7280;
}

.markdown-content :deep(code) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: monospace;
    font-size: 0.875em;
}

.markdown-content :deep(pre) {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.75rem;
    border-radius: 0.375rem;
    overflow-x: auto;
    margin: 0.5rem 0;
}

.markdown-content :deep(pre code) {
    background-color: transparent;
    padding: 0;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
    font-weight: 600;
    margin: 0.75rem 0 0.5rem 0;
}

.markdown-content :deep(h1) { font-size: 1.5em; }
.markdown-content :deep(h2) { font-size: 1.3em; }
.markdown-content :deep(h3) { font-size: 1.15em; }
.markdown-content :deep(h4) { font-size: 1.05em; }
.markdown-content :deep(h5) { font-size: 1em; }
.markdown-content :deep(h6) { font-size: 0.95em; }
</style>
